// @ts-strict-ignore
import {TranslateService} from "@ngx-translate/core";
import {EventEmitterService} from "@event-emitter";
import {
	SimConfigurationService,
	SimDeviceService,
	SimExportService,
	SimHealthMonitorExtendedService,
	SimInjectableDataService,
	SimMaterialDialogService,
	NotificationService,
	SimPromiseDelayService,
	SimUserExtendedService,
	SimUtilityService,
	SimWifiService,
	TrackByMethods,
} from "@services";
import {Router} from "@angular/router";
import {
	ExportState,
	ExportView,
	ExportVisualState,
	GetCurrentExportsInternalResponse,
	HealthErrorCodes,
	Modules,
	Permissions,
	Scope,
	ServiceType,
	SimUser,
	SimVersion,
	SystemStatusView,
	WebSettings,
	WifiAccessPointResponse,
	WifiData,
} from "@models";
import {AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {DialogData_SettingsDialog} from "../../../modules/dialog/material-dialogs/settings-dialog/settings-dialog.component";
import {
	bounceAnimation,
	fadeInExpandOnEnterAnimation,
	fadeOutCollapseOnLeaveAnimation,
	zoomInOnEnterAnimation,
	zoomOutOnLeaveAnimation,
} from "angular-animations";
import {animate, query, stagger, style, transition, trigger} from "@angular/animations";
import {Translations} from "@translations";
import {timer} from "rxjs";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {DialogData_DeleteDialogOutput} from "../../../modules/dialog/material-dialogs/general-dialogs/delete-dialog-mat/delete-dialog-mat.component";
import Enumerable from "linq";
import {ClientAudioDeviceComponent} from "./client-audio-device/client-audio-device/client-audio-device.component";
import {isEqual} from "lodash-es";

export interface Notification {
	type: NotificationType;
	category: NotificationCategory;
	title: string;
	message: string | string[];
	notificationTime: Date;
}

export enum NotificationType {
	INFO,
	WARNING,
	ERROR,
}

export enum NotificationCategory {
	HEALTH_ERROR,
	UPDATE_AVAILABLE,
	SCHEDULER_ERROR,
	VERSION_MISMATCH,
}

export enum ShowContainer {
	None = 0,
	Export,
	Notification,
	AudioDevice,
	Wifi,
}

@UntilDestroy()
@Component({
	selector: "sim-toolbar-menu",
	templateUrl: "./toolbar-menu.component.html",
	styleUrls: ["./toolbar-menu.component.scss"],
	providers: [EventEmitterService],
	animations: [
		zoomInOnEnterAnimation({duration: 250}),
		zoomOutOnLeaveAnimation({duration: 250}),
		bounceAnimation(),
		fadeInExpandOnEnterAnimation(),
		fadeOutCollapseOnLeaveAnimation(),
		trigger("listStagger", [
			transition("* <=> *", [
				query(
					":enter",
					[
						style({
							opacity: 0,
							transform: "translateY(-15px)",
						}),
						stagger(
							"175ms",
							animate(
								"550ms ease-out",
								style({
									opacity: 1,
									transform: "translateY(0px)",
								}),
							),
						),
					],
					{optional: true},
				),
				query(":leave", animate("200ms", style({opacity: 0})), {
					optional: true,
				}),
			]),
		]),
	],
	standalone: false,
})
export class ToolbarMenuComponent implements OnInit, AfterViewInit, OnDestroy {
	T = Translations;
	@Input() SettingButtonShow: boolean = false;

	// Enums
	readonly Permissions = Permissions;
	readonly ShowContainer = ShowContainer;
	readonly NotificationType = NotificationType;
	readonly NotificationCategory = NotificationCategory;
	readonly SimUtilityService = SimUtilityService;
	readonly ExportState = ExportState;
	readonly TrackByMethods = TrackByMethods;
	readonly ExportVisualState = ExportVisualState;
	readonly Modules = Modules;
	readonly Scope = Scope;

	// Methods
	readonly settings: WebSettings = SimConfigurationService.settings;
	readonly isScope = SimConfigurationService.isScope;
	readonly hasLicenseModule = SimConfigurationService.hasLicenseModule;
	readonly Math = Math;
	readonly Array = Array;

	// Public properties
	public user: SimUser;
	public animateNotification = false;
	public localTime: Date;
	public showContainer: ShowContainer = ShowContainer.None;
	public notifications: Notification[] = [];
	public readonly langDict: {[key: string]: string};
	public exportsInProgress: number = 0;
	public allExports: ExportView[] = null;
	public runningExport: ExportView = null;
	public exportPaused: boolean = false;
	public loadingExports: boolean = false;
	public setActiveLang: string;
	public serverWifiNetworks: WifiAccessPointResponse[] = [];
	public currentServerWifi: WifiAccessPointResponse = null;
	public currentClientWifi: WifiData = null;
	public isWifiManagementEnabled: boolean = false;
	public showPermissionTooltips: boolean = false;

	// Private properties
	private showTranslationKeys: boolean = false;
	private updateInterval: number;
	private readonly ShowAutoUpdate: boolean;
	private readonly ShowVersionMismatchErrors: boolean;

	@ViewChild("clientAudioDeviceComponent") private clientAudioDeviceComponent: ClientAudioDeviceComponent;

	constructor(
		public translate: TranslateService,
		public userService: SimUserExtendedService,
		private router: Router,
		private _simNotificationService: NotificationService,
		private _emitterService: EventEmitterService,
		private _matDialogService: SimMaterialDialogService,
		private _simPromiseDelayService: SimPromiseDelayService,
		private _simExportService: SimExportService,
		private eRef: ElementRef,
		private _wifiService: SimWifiService,
		private _simInjectableDataService: SimInjectableDataService,
		private _healthMonitorService: SimHealthMonitorExtendedService,
		private _simDeviceService: SimDeviceService,
	) {
		this.user = this.userService.user;

		this.userService.userChangedEmitter.pipe(untilDestroyed(this)).subscribe((args) => {
			this.user = args.user;
		});

		this._emitterService.onToggleShowTranslationKeys.subscribe((value) => {
			this.showTranslationKeys = value.visible;
		});

		this._emitterService.onToggleShowPermissionTooltips.subscribe((value) => {
			this.showPermissionTooltips = value.visible;
		});

		this._emitterService.onDeviceSettingChanged.subscribe((value) => {
			if (value.deviceSettings.currentLanguage) {
				this.setActiveLang = value.deviceSettings.currentLanguage;
			}
		});

		//languages in their native translation
		this.langDict = {
			en: "English",
			de: "Deutsch",
			fr: "Français",
			ru: "Русский",
			pl: "Polski",
			es: "Español",
			he: "עברית"
		};

		this.loadCurrentExports(true);

		this.initExportEventHandler();

		this.SettingButtonShow = this.userService.hasPermission(Permissions.UserSettings);
		this.ShowAutoUpdate = this.userService.hasPermission(Permissions.AutoUpdate_Access);
		this.ShowVersionMismatchErrors = this.userService.user.isHost;

		this.isWifiManagementEnabled = SimConfigurationService.settings.enableWifiManagement;

		this._simInjectableDataService
			.getClientWifi()
			.pipe(untilDestroyed(this))
			.subscribe((data) => {
				this.currentClientWifi = data;
			});

		if (this.isWifiManagementEnabled) {
			this.loadWifiNetworks();
		}
	}

	ngAfterViewInit(): void {
		this._healthMonitorService.requestSystemStatusOverWs().then();
		this._emitterService.onHealthReported.subscribe((value) => {
			this.generateNotificationFromHealthReport(value.systemStatusView);
		});

		this._emitterService.onNewUpdateAvailable.subscribe((versionNumber: {versionNumber: SimVersion}) => {
			if (!this.ShowAutoUpdate) return;

			const versionString = `${versionNumber.versionNumber.major}.${versionNumber.versionNumber.minor}.${versionNumber.versionNumber.patch}-${versionNumber.versionNumber.build}`;
			const notification: Notification = {
				title: `SIMStation Update available!`,
				category: NotificationCategory.UPDATE_AVAILABLE,
				type: NotificationType.INFO,
				notificationTime: new Date(),
				message: `Version: ${versionString}`,
			};
			this.upSertNotification(notification);
		});

		// Check update
		this.setActiveLang = this.translate.currentLang;
	}

	ngOnInit() {
		this.localTime = new Date();
		this.updateInterval = setInterval(() => {
			this.localTime = new Date();
		}, 1000);

		timer(0, 4000)
			.pipe(untilDestroyed(this))
			.subscribe(async () => {
				if (!!this.runningExport) {
					this.loadCurrentExports(true);
				}
			});
	}

	openSettingsDialog() {
		let data: DialogData_SettingsDialog = {
			route: this.router.url,
			width: 80,
		};
		this._matDialogService.openSettingsDialog(data, {width: "1300px"}).then();
	}

	openVersionInfoDialog(): void {
		this._matDialogService.openVersionDialog({align: "right"}).then();
	}

	containerIconClicked(showContainer: ShowContainer): void {
		if (showContainer === ShowContainer.AudioDevice) {
			if (this.showContainer == showContainer) {
				this.clientAudioDeviceComponent.closeComponent.emit();
				return;
			} else {
				this.clientAudioDeviceComponent.openComponent().then();
			}
		}

		if (this.showContainer === showContainer) {
			this.showContainer = ShowContainer.None;
		} else {
			this.showContainer = ShowContainer.None;
			setTimeout(() => {
				this.showContainer = showContainer;

				if (this.showContainer == ShowContainer.Wifi) {
					this.loadWifiNetworks().then();
				}
			}, 200);
		}
	}

	updateSimStationNotificationClicked(): void {
		this.containerIconClicked(ShowContainer.None);
		this._matDialogService.openUpdateSimStationDialog({align: "right"}).then();
	}

	openPowerOffDialog(): void {
		this._matDialogService.openPowerOffDialog({}).then();
	}

	toggleTranslationKeys(): void {
		this.showTranslationKeys = !this.showTranslationKeys;
		this._emitterService.onToggleShowTranslationKeys.next({
			visible: this.showTranslationKeys,
		});
	}

	togglePermissionTooltips(): void {
		this.showPermissionTooltips = !this.showPermissionTooltips;
		this._emitterService.onToggleShowPermissionTooltips.next({
			visible: this.showPermissionTooltips,
		});
	}

	changeLanguage(langKey: string): void {
		this._simDeviceService
			.updateDeviceLanguage(langKey)
			.then(() => {
				// Language will be updated when server sends DeviceSettingChanged event
			})
			.catch((error) => {
				console.error("Failed to update device language:", error);
			});
		this._emitterService.onToggleShowTranslationKeys.next({visible: false});
	}

	public loadCurrentExports(immediate: boolean = false): void {
		// Local function definition
		const getExportFunc = () =>
			this._simExportService
				.getCurrentExports(null, 5, false)
				.then((getCurrentExportsResult: GetCurrentExportsInternalResponse) => {
					if (getCurrentExportsResult) {
						const oldExports = this.allExports;

						this.allExports = getCurrentExportsResult.all;
						this.runningExport = getCurrentExportsResult.running;
						this.exportPaused = getCurrentExportsResult.isPaused;

						const oldFinishedExports = Enumerable.from(oldExports).where((x) => x.exportState == ExportState.Finished);
						const newFinishedExports = Enumerable.from(this.allExports).where((x) => x.exportState == ExportState.Finished);

						newFinishedExports
							.where((x) => !oldFinishedExports.any((y) => y.id == x.id))
							.select((x) => x.scenarioRecordingId)
							.distinct()
							.forEach((scenarioRecordingId) => this._emitterService.onNotifyExport.next({scenarioRecordingId}));
					}
				})
				.catch((err) => console.debug(err));
		if (!immediate) {
			this._simPromiseDelayService.callWithMinimunDelay(
				"getCurrentExports-menu",
				getExportFunc,
				this.showContainer == ShowContainer.Export ? 2000 : 10000,
			);
		} else {
			getExportFunc().then();
		}
	}

	public deleteExport(exportView: ExportView): void {
		this._matDialogService
			.openDeleteDialog({
				deleteTitle: this.translate.instant(this.T.EXPORT_DIALOG_DELETE_TITLE),
				deleteText: this.translate.instant(this.T.EXPORT_DIALOG_DELETE_DESCRIPTION),
				deleteButtonText: this.translate.instant(this.T.GLOBAL_TEXT_YES),
				cancelText: this.translate.instant(this.T.GLOBAL_TEXT_NO),
			})
			.then((data: DialogData_DeleteDialogOutput) => {
				if (data.action !== "ok") return;
				this._simExportService.deleteExport({exportProcessInfoId: exportView.id}).then((deleteExportResult: boolean) => {
					if (deleteExportResult) {
						this._emitterService.onNotifyExport.next({scenarioRecordingId: exportView.scenarioRecordingId});
						this.loadCurrentExports(true);
					}
				});
			});
	}

	public downloadExport(exportId: string): void {
		window.location.href = this._simExportService.downloadExport_Url(exportId);
	}

	public pauseExport(exportId: string): void {
		this._simExportService.pause({exportProcessInfoId: exportId}).then(() => {
			this.loadCurrentExports(true);
		});
	}

	public dismissExport(exportId: string): void {
		this._simExportService.dismiss({exportProcessInfoId: exportId}).then(() => {
			this.loadCurrentExports(true);
		});
	}

	public cancelExport(exportId: string) {
		this._matDialogService
			.openDeleteDialog({
				deleteTitle: this.translate.instant(this.T.EXPORT_DIALOG_CANCEL_TITLE),
				deleteText: this.translate.instant(this.T.EXPORT_DIALOG_CANCEL_DESCRIPTION),
				deleteButtonText: this.translate.instant(this.T.GLOBAL_TEXT_YES),
				cancelText: this.translate.instant(this.T.GLOBAL_TEXT_NO),
			})
			.then((data: DialogData_DeleteDialogOutput) => {
				if (data.action == "ok") {
					this._simExportService.cancel({exportProcessInfoId: exportId}).then(() => {
						this.loadCurrentExports(true);
					});
				}
			});
	}

	public resumeExport(exportId: string) {
		this._simExportService.resume({exportProcessInfoId: exportId}).then(() => {
			this.loadCurrentExports(true);
		});
	}

	public hasOnlyDismissedExports(): boolean {
		if (this.allExports?.length > 0) {
			return this.allExports.some((exportView) => exportView.visualState !== ExportVisualState.Dismissed);
		} else {
			return true;
		}
	}

	@HostListener("document:click", ["$event"]) onClick(event) {
		if (!this.eRef.nativeElement.contains(event.target)) {
			if (this._matDialogService.getOpenDialogs().length > 0) return;
			this.showContainer = ShowContainer.None;
		}
	}

	public async connectToWifiNetwork(network: WifiAccessPointResponse): Promise<void> {
		network.hasProfile ? await this.setupProfileConnection(network) : await this.setupGuestConnection(network);
	}

	public async disconnect() {
		await this._wifiService.disconnect();
		return this.loadWifiNetworks();
	}

	public async forget(network: WifiAccessPointResponse): Promise<void> {
		await this._wifiService.removeProfile({
			accessPointName: network.name,
		});
		return this.loadWifiNetworks();
	}

	ngOnDestroy(): void {
		clearInterval(this.updateInterval);
	}

	private async setupGuestConnection(network: WifiAccessPointResponse) {
		const result = await this._matDialogService.openConnectWifiDialog({
			wifiNetwork: network,
		});

		if (result.action !== "ok") {
			return;
		}

		const {action, ...params} = result;

		const connectionResult = await this._wifiService.connectToAccessPoint({
			accessPointName: network.name,
			...params,
		});

		connectionResult.success
			? this._simNotificationService.notifySuccess(this.translate.instant(this.T.SHARED_MENU_CONNECTED_TO_WIFI))
			: this._simNotificationService.notifyError(this.translate.instant(this.T.SHARED_MENU_COULD_NOT_CONNECT_TO_WIFI));
		await this.loadWifiNetworks();
	}

	private async setupProfileConnection(network: WifiAccessPointResponse) {
		const connectionResult = await this._wifiService.connectToAccessPointWithProfile({
			accessPointName: network.name,
		});

		connectionResult.success
			? this._simNotificationService.notifySuccess(this.translate.instant(this.T.SHARED_MENU_CONNECTED_TO_WIFI))
			: this._simNotificationService.notifyError(this.translate.instant(this.T.SHARED_MENU_COULD_NOT_CONNECT_TO_WIFI));

		await this.loadWifiNetworks();
	}

	private generateNotificationFromHealthReport(systemReport: SystemStatusView) {
		//COMPONENTS
		systemReport.components.forEach((componentStatusView) => {
			if (isEqual([HealthErrorCodes.VersionMismatch], componentStatusView.status.errorCodes) && !this.ShowVersionMismatchErrors) {
				return;
			}
			const notification: Notification = {
				type: NotificationType.ERROR,
				category: NotificationCategory.HEALTH_ERROR,
				message:
					componentStatusView.status.errorCodes?.length > 0
						? this._healthMonitorService.getErrorMessage(componentStatusView.status.errorCodes[0])
						: null,
				title: ServiceType[componentStatusView.serviceType],
				notificationTime: new Date(),
			};
			this.upSertNotification(notification);
		});

		//SCHEDULER
		this.upSertNotification({
			title: "NOTIFICATION_ERROR_CRITICAL_SCHEDULER_DISABLED",
			message: systemReport.scheduler,
			notificationTime: new Date(),
			type: NotificationType.ERROR,
			category: NotificationCategory.SCHEDULER_ERROR,
		});

		//VERSIONS
		if (this.ShowVersionMismatchErrors) {
			let message: string | string[] = "";
			if (!systemReport.versions?.areVersionsCompatible) {
				message = systemReport.versions.componentVersions.map(
					(x) => `${ServiceType[x.serviceType]} ${x.isCompatible ? "✔" : "❌"}: ${x.version ?? "[NA]"} `,
				);
			}
			this.upSertNotification({
				title: "NOTIFICATION_ERROR_APPLICATION_VERSION_MISMATCH",
				message: message,
				notificationTime: new Date(),
				type: NotificationType.ERROR,
				category: NotificationCategory.VERSION_MISMATCH,
			});
		}
	}

	// updates, adds or removes the notification, based on title and category (remove if message is empty)
	private upSertNotification(notification: Notification): void {
		let index = this.notifications?.findIndex(
			(value) => value.category === notification.category && value.title === notification.title,
		);

		if (index === -1 && !this.isMessageEmtpy(notification)) {
			//add new
			this.notifications.push(notification);
		} else if (!this.isMessageEmtpy(notification)) {
			//update existing
			const current = this.notifications[index];
			current.message = notification.message;
			current.notificationTime = notification.notificationTime;
			current.type = notification.type;
		} else if (index > -1) {
			//remove existing
			this.notifications.splice(index, 1);
		}
	}

	private isMessageEmtpy(notification: Notification): boolean {
		return !notification.message || notification.message.length == 0;
	}

	private initExportEventHandler(): void {
		this._emitterService.onNotifyExportProgress.subscribe(() => {
			this.loadCurrentExports();
		});
	}

	private async loadWifiNetworks(): Promise<void> {
		this.serverWifiNetworks = await this._wifiService.getAccessPoints();
		this.currentServerWifi = this.serverWifiNetworks.find((w) => w.isConnected);
	}

	public getClientWifiIcon(currentClientWifi: WifiData) {
		if (currentClientWifi?.strength <= 33.3) {
			return "fa-wifi-1";
		} else if (currentClientWifi?.strength <= 66.6) {
			return "fa-wifi-2";
		} else if (currentClientWifi?.strength > 66.6) {
			return "fa-wifi";
		}
	}

	public getServerWifiIcon(currentServerWifi: WifiAccessPointResponse) {
		if (currentServerWifi?.signalStrength <= 33.3) {
			return "fa-wifi-1";
		} else if (currentServerWifi?.signalStrength <= 66.6) {
			return "fa-wifi-2";
		} else if (currentServerWifi?.signalStrength > 66.6) {
			return "fa-wifi";
		}
	}
}
